import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import { ArrowBack, Visibility, VisibilityOff, CheckCircle, Error } from '@mui/icons-material'
import Button from '@components/common/Button'
import api from '@services/api'
import { toast } from 'react-toastify'

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [tokenError, setTokenError] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [success, setSuccess] = useState(false)
  const [userInfo, setUserInfo] = useState({ email: '', first_name: '' })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      new_password: '',
      new_password_confirm: '',
    },
  })

  const newPassword = watch('new_password')

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('No reset token provided.')
        setValidating(false)
        return
      }

      try {
        const response = await api.get(`/auth/reset-password/?token=${token}`)
        if (response.data.valid) {
          setTokenValid(true)
          setUserInfo({
            email: response.data.email,
            first_name: response.data.first_name,
          })
        } else {
          setTokenError(response.data.error || 'Invalid or expired token.')
        }
      } catch (err) {
        setTokenError('Failed to validate reset token.')
      } finally {
        setValidating(false)
      }
    }

    validateToken()
  }, [token])

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      await api.post('/auth/reset-password/', {
        token,
        new_password: data.new_password,
        new_password_confirm: data.new_password_confirm,
      })
      setSuccess(true)
      toast.success('Password reset successfully!')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset password.')
    } finally {
      setLoading(false)
    }
  }

  if (validating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F5F7FA',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F7FA',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Logo */}
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              width: 60,
              height: 60,
              backgroundColor: '#1B5E7A',
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" fontWeight={700} color="white">
              UCI
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Reset Password
          </Typography>
        </Box>

        {/* Token Invalid */}
        {!tokenValid && tokenError && (
          <Box textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#FFEBEE',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Error sx={{ fontSize: 32, color: '#F44336' }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Invalid Reset Link
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {tokenError}
            </Typography>
            <Button
              component={Link}
              to="/forgot-password"
              fullWidth
              sx={{ mb: 2 }}
            >
              Request New Reset Link
            </Button>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              startIcon={<ArrowBack />}
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        )}

        {/* Success */}
        {success && (
          <Box textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#E8F5E9',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CheckCircle sx={{ fontSize: 32, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Password Reset Successfully
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Your password has been changed. You can now log in with your new password.
            </Typography>
            <Button
              component={Link}
              to="/login"
              fullWidth
            >
              Go to Login
            </Button>
          </Box>
        )}

        {/* Reset Form */}
        {tokenValid && !success && (
          <>
            {userInfo.first_name && (
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                Hi {userInfo.first_name}, create your new password below.
              </Typography>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('new_password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.new_password}
                helperText={errors.new_password?.message}
                margin="normal"
                autoComplete="new-password"
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...register('new_password_confirm', {
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === newPassword || 'Passwords do not match',
                })}
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                error={!!errors.new_password_confirm}
                helperText={errors.new_password_confirm?.message}
                margin="normal"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                Reset Password
              </Button>
            </form>

            <Box textAlign="center" mt={2}>
              <Button
                component={Link}
                to="/login"
                variant="text"
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back to Login
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default ResetPassword
